import { useEffect, useMemo, useState } from 'react';
import fundOpportunityContentCache from 'FundOpportunityContentCache';
import type { ContentItem } from '../../models/ContentManagementInterface';

/** `images/...`, `/images/...` or `/api/images/...` — app-served asset paths that need signing. */
const IMAGE_PATH = /^\/?(?:api\/)?images?\//i;

/** path → resolved signed URL, shared across every preview mount. */
const signedCache = new Map<string, string>();
const inFlight = new Map<string, Promise<string>>();

function resolveOne(path: string): Promise<string> {
  const cached = signedCache.get(path);
  if (cached) return Promise.resolve(cached);
  let pending = inFlight.get(path);
  if (!pending) {
    pending = fundOpportunityContentCache
      .getAssetImageUrl(path)
      .then((signed: string) => {
        const url = signed || path;
        signedCache.set(path, url);
        return url;
      })
      .catch(() => path)
      .finally(() => inFlight.delete(path));
    inFlight.set(path, pending);
  }
  return pending;
}

function collectImagePaths(node: unknown, into: Set<string>): void {
  if (Array.isArray(node)) {
    node.forEach((n) => collectImagePaths(n, into));
  } else if (node && typeof node === 'object') {
    Object.values(node as Record<string, unknown>).forEach((v) => collectImagePaths(v, into));
  } else if (typeof node === 'string' && IMAGE_PATH.test(node)) {
    into.add(node);
  }
}

function swapImagePaths<T>(node: T, map: Map<string, string>): T {
  if (Array.isArray(node)) {
    return node.map((n) => swapImagePaths(n, map)) as unknown as T;
  }
  if (node && typeof node === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
      if (key === '__proto__' || key === 'constructor' || key === 'prototype') continue;
      out[key] = swapImagePaths(value, map);
    }
    return out as T;
  }
  if (typeof node === 'string' && map.has(node)) {
    return map.get(node) as unknown as T;
  }
  return node;
}

/**
 * The App preview renders the real native components, whose `<Image>` loads a
 * URL directly with no auth header — so an `/api/images/...` path is rejected by
 * the image route and the slot renders blank (the Web preview works because it
 * goes through `<AssetImage>`). Resolve every image path in the content to a
 * signed URL (same cache `<AssetImage>` uses) and hand the rewritten content to
 * the native renderer. Keyed on the set of image paths, not on `content`
 * identity, so the editor's frequent re-renders don't restart the resolve.
 */
export function usePreviewResolvedImages(content: ContentItem[]): ContentItem[] {
  const paths = useMemo(() => {
    const set = new Set<string>();
    collectImagePaths(content, set);
    return [...set];
  }, [content]);
  const pathsKey = paths.join('\n');

  const [signed, setSigned] = useState<Map<string, string>>(() => new Map(signedCache));

  useEffect(() => {
    if (paths.length === 0) return undefined;
    let cancelled = false;
    Promise.all(paths.map((path) => resolveOne(path).then((url) => [path, url] as const))).then(
      (pairs) => {
        if (!cancelled) setSigned(new Map([...signedCache, ...pairs]));
      },
    );
    return () => {
      cancelled = true;
    };
    // paths is derived from pathsKey; depending on the string keeps this stable
    // across the editor's frequent re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathsKey]);

  return useMemo(
    () => (signed.size ? swapImagePaths(content, signed) : content),
    [content, signed],
  );
}
